import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/shared/ui/theme/ThemeProvider';
import { useProfile } from '@/modules/profiles/presentation/context/ProfileContext';
import { BackButton } from '@/presentation/components/BackButton';
import { DateRangeFilter } from '@/presentation/components/DateRangeFilter';
import { useTimelogsByAccessRoleQuery } from '@/modules/attendance/application/timelogQueries';
import { GlassLoader } from '@/shared/ui/components/GlassLoader';
import type { Timelog } from '@/modules/attendance/domain/entities/Timelog';
import { TimelogDetailModal } from '@/modules/employees/presentation/components/TimelogDetailModal';
import { defaultRange } from '@/shared/utils/datetime';
import { retryAllWithinWindow } from '@/shared/sync/retryFailed';
import { useToast } from '@/shared/ui/toast/ToastContext';
import { exportTimelogsCsv } from '../utils/exportTimelogsCsv';
import { AdminMarcajeRow } from '../components/AdminMarcajeRow';

type TipoFilter = 'todos' | 'entrada' | 'salida';

export function AdminMarcajesPage() {
  const { C, T } = useTheme();
  const navigate = useNavigate();
  const { selectedRole } = useProfile();
  const { t } = useTranslation('common');
  const { showToast } = useToast();

  const __dr = defaultRange();
  const [from, setFrom] = useState(__dr.from);
  const [to, setTo] = useState(__dr.to);
  const [tipoFilter, setTipoFilter] = useState<TipoFilter>('todos');
  const [detail, setDetail] = useState<Timelog | null>(null);
  const [resendingAll, setResendingAll] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const handleResendAll = async () => {
    if (resendingAll) return;
    setResendingAll(true);
    try {
      const count = await retryAllWithinWindow();
      if (count === 0) {
        showToast(t('admin.timelogResendAllNone'), { variant: 'info' });
      } else if (!navigator.onLine) {
        // syncNow() no-opea sin conexión — quedaron en cola, no se mandaron.
        showToast(t('admin.timelogResendAllOffline', { count }), { variant: 'warning' });
      } else {
        // 'info', not 'success' — retryAllWithinWindow() only confirms the items were
        // re-queued, not that the backend actually accepted them; a 'success' badge here
        // would overclaim delivery before the sync worker has even attempted it.
        showToast(t('admin.timelogResendAllQueued', { count }), { variant: 'info' });
      }
    } finally {
      setResendingAll(false);
    }
  };

  const { data: timelogs = [], isFetching } = useTimelogsByAccessRoleQuery(
    selectedRole?.idAccessRole ?? null, from, to,
  );

  const counts = useMemo(() => ({
    todos: timelogs.length,
    entrada: timelogs.filter((tl) => tl.accessType === 1).length,
    salida: timelogs.filter((tl) => tl.accessType === 2).length,
  }), [timelogs]);

  const filtered = useMemo(() => {
    if (tipoFilter === 'todos') return timelogs;
    if (tipoFilter === 'entrada') return timelogs.filter((tl) => tl.accessType === 1);
    return timelogs.filter((tl) => tl.accessType === 2);
  }, [timelogs, tipoFilter]);

  const handleExportCsv = () => {
    if (filtered.length === 0) {
      showToast(t('admin.timelogExportNone'), { variant: 'info' });
      return;
    }
    exportTimelogsCsv(filtered);
  };

  const TIPO_FILTERS: { k: TipoFilter; l: string; c: string }[] = [
    { k: 'todos',   l: t('admin.filterAll'),     c: C.primary },
    { k: 'entrada', l: t('admin.filterEntries'), c: C.success },
    { k: 'salida',  l: t('admin.filterExits'),   c: C.danger  },
  ];

  return (
    <div style={{ flex: 1, background: T.bg, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '24px 30px 14px', borderBottom: `1px solid ${T.border}`, background: T.surfaceBg, flexShrink: 0 }}>
        <div style={{ maxWidth: 645, margin: '0 auto', width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16, position: 'relative' }}>
            <BackButton T={T} onClick={() => { void navigate('/admin'); }} />
            <div style={{ position: 'absolute', left: 0, right: 0, textAlign: 'center', fontSize: 14, fontWeight: 700, color: T.text, pointerEvents: 'none' }}>
              {t('admin.timelogTitle')}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', flex: 1 }}>
              {TIPO_FILTERS.map((f) => (
                <button
                  key={f.k}
                  onClick={() => { setTipoFilter(f.k); }}
                  style={{ padding: '5px 11px', borderRadius: 14, border: `1px solid ${tipoFilter === f.k ? f.c : T.border}`, background: tipoFilter === f.k ? `${f.c}18` : 'transparent', color: tipoFilter === f.k ? f.c : T.textMuted, fontFamily: 'inherit', fontSize: 9, fontWeight: 700, cursor: 'pointer', transition: 'all .15s' }}
                >
                  {f.l} · {counts[f.k]}
                </button>
              ))}
            </div>
            {/* Kept as one flex item so it wraps as a single unit onto its own line on
                narrow viewports; marginLeft:'auto' right-aligns it whether it shares the
                row with the chips (wide) or wraps alone (narrow) — this guarantees the
                kebab button (and its right-anchored dropdown) never lands near the left
                edge of the screen. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginLeft: 'auto' }}>
              <DateRangeFilter
                value={{ from, to }}
                onChange={({ from: newFrom, to: newTo }) => {
                  setFrom(newFrom);
                  setTo(newTo);
                }}
              />
              <div style={{ position: 'relative' }}>
                <button
                  type="button"
                  onClick={() => { setMenuOpen((o) => !o); }}
                  title={t('admin.timelogActions')}
                  aria-label={t('admin.timelogActions')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 30,
                    height: 30,
                    borderRadius: 10,
                    border: `1px solid ${T.border}`,
                    background: menuOpen ? T.rowBg : 'transparent',
                    color: T.textMuted,
                    cursor: 'pointer',
                  }}
                >
                  <svg width={16} height={16} viewBox="0 0 24 24" fill="currentColor">
                    <circle cx="12" cy="5" r="2" />
                    <circle cx="12" cy="12" r="2" />
                    <circle cx="12" cy="19" r="2" />
                  </svg>
                </button>

                {menuOpen && (
                  <>
                    {/* click-outside backdrop */}
                    <div
                      onClick={() => { setMenuOpen(false); }}
                      style={{ position: 'fixed', inset: 0, zIndex: 20 }}
                    />
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        marginTop: 6,
                        minWidth: 190,
                        maxWidth: 'calc(100vw - 40px)',
                        background: T.surfaceBg,
                        border: `1px solid ${T.border}`,
                        borderRadius: 10,
                        boxShadow: T.shadow,
                        overflow: 'hidden',
                        zIndex: 21,
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => { setMenuOpen(false); void handleResendAll(); }}
                        disabled={resendingAll}
                        style={{
                          display: 'block',
                          width: '100%',
                          textAlign: 'left',
                          padding: '9px 14px',
                          border: 'none',
                          background: 'transparent',
                          color: T.text,
                          fontFamily: 'inherit',
                          fontSize: 11,
                          fontWeight: 600,
                          cursor: resendingAll ? 'default' : 'pointer',
                          opacity: resendingAll ? 0.6 : 1,
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = T.rowBg; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        {resendingAll ? t('admin.timelogResendingAll') : t('admin.timelogResendAll')}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setMenuOpen(false); handleExportCsv(); }}
                        style={{
                          display: 'block',
                          width: '100%',
                          textAlign: 'left',
                          padding: '9px 14px',
                          border: 'none',
                          borderTop: `1px solid ${T.border}`,
                          background: 'transparent',
                          color: T.text,
                          fontFamily: 'inherit',
                          fontSize: 11,
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = T.rowBg; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        {t('admin.timelogExportCsv')}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* List */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '14px 30px 30px' }}>
        <div style={{ maxWidth: 645, margin: '0 auto', position: 'relative', overflow: 'hidden' }}>
          {filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '45px 0', color: T.textMuted, fontSize: 11 }}>{t('admin.noRecords')}</div>
          ) : (
            filtered.map((tl) => (
              <AdminMarcajeRow
                key={tl.idTimelog}
                tl={tl}
                onOpenDetail={setDetail}
                T={T}
                C={C}
                t={t}
              />
            ))
          )}
          {isFetching && <GlassLoader T={T} />}
        </div>
      </div>

      {detail && detail.employee && (
        <TimelogDetailModal
          timelog={detail}
          initials={detail.employee.initials}
          avatarColor={detail.employee.avatarColor}
          onClose={() => { setDetail(null); }}
          T={T}
          C={C}
        />
      )}
    </div>
  );
}
